/**
 * Minimal IndexedDB access for two local stores:
 * - `journal`: unsaved scenes per user/design (PRD E09 local recovery journal; cleared on logout, 7-day expiry).
 * - `demo`: the anonymous demo draft + the image blobs the visitor picked (D01).
 * Writes are coalesced per key (latest wins) so typing does not queue one write per keystroke.
 */
import type { Scene } from '@xcreative/scene-core';
import type { JournalEntry, SaveJournal } from '../editor/saveController.js';

const DB_NAME = 'xcreative';
const DB_VERSION = 1;
const STORES = ['journal', 'demo'] as const;
type StoreName = (typeof STORES)[number];

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB ไม่พร้อมใช้งานในเบราว์เซอร์นี้'));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      for (const s of STORES) if (!req.result.objectStoreNames.contains(s)) req.result.createObjectStore(s);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('เปิด IndexedDB ไม่ได้'));
  });
  dbPromise.catch(() => (dbPromise = null));
  return dbPromise;
}

function tx<T>(store: StoreName, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T> | void): Promise<T | undefined> {
  return openDb().then(
    (db) =>
      new Promise<T | undefined>((resolve, reject) => {
        const t = db.transaction(store, mode);
        const req = fn(t.objectStore(store));
        let result: T | undefined;
        if (req) req.onsuccess = () => (result = req.result);
        t.oncomplete = () => resolve(result);
        t.onerror = () => reject(t.error ?? new Error('IndexedDB error'));
        t.onabort = () => reject(t.error ?? new Error('IndexedDB aborted (พื้นที่เก็บข้อมูลอาจเต็ม)'));
      }),
  );
}

export const idbGet = <T>(store: StoreName, key: string) => tx<T>(store, 'readonly', (s) => s.get(key) as IDBRequest<T>);
export const idbPut = (store: StoreName, key: string, value: unknown) => tx(store, 'readwrite', (s) => void s.put(value, key)).then(() => undefined);
export const idbDelete = (store: StoreName, key: string) => tx(store, 'readwrite', (s) => void s.delete(key)).then(() => undefined);
export const idbClear = (store: StoreName) => tx(store, 'readwrite', (s) => void s.clear()).then(() => undefined);

// ---------- recovery journal ----------

const JOURNAL_TTL_MS = 7 * 24 * 3600 * 1000;

export interface StoredJournal extends JournalEntry {
  userId: string;
}

/** SaveJournal backed by IndexedDB, keyed per user + design; coalesces rapid writes. */
export class IdbJournal implements SaveJournal {
  private pending = new Map<string, StoredJournal | null>();
  private writing = new Set<string>();
  onError: ((e: unknown) => void) | null = null;

  constructor(private readonly userId: string) {}

  private key(designId: string) {
    return `${this.userId}:${designId}`;
  }

  put(entry: JournalEntry): void {
    this.enqueue(this.key(entry.designId), { ...entry, userId: this.userId });
  }

  clear(designId: string): void {
    this.enqueue(this.key(designId), null);
  }

  private enqueue(key: string, value: StoredJournal | null) {
    this.pending.set(key, value);
    if (!this.writing.has(key)) void this.drain(key);
  }

  private async drain(key: string) {
    this.writing.add(key);
    try {
      while (this.pending.has(key)) {
        const value = this.pending.get(key)!;
        this.pending.delete(key);
        try {
          if (value) await idbPut('journal', key, value);
          else await idbDelete('journal', key);
        } catch (e) {
          this.onError?.(e);
        }
      }
    } finally {
      this.writing.delete(key);
    }
  }

  async read(designId: string): Promise<StoredJournal | null> {
    try {
      const v = await idbGet<StoredJournal>('journal', this.key(designId));
      if (!v) return null;
      if (Date.now() - v.updatedAt > JOURNAL_TTL_MS) {
        await idbDelete('journal', this.key(designId));
        return null;
      }
      return v;
    } catch {
      return null;
    }
  }
}

/** PRD C03: local recovery is cleared on logout. */
export const clearAllJournals = () => idbClear('journal').catch(() => undefined);

// ---------- anonymous demo draft ----------

export interface DemoDraft {
  scene: Scene;
  title: string;
  updatedAt: number;
  /** Local-only images the visitor picked (or sample images fetched for the demo), by local assetId. */
  assets: Record<string, Blob>;
}

const DEMO_KEY = 'draft';

export const loadDemoDraft = () => idbGet<DemoDraft>('demo', DEMO_KEY).then((v) => v ?? null).catch(() => null);
/** Rejects on quota/availability errors — callers must not show "saved" then (D01). */
export const saveDemoDraft = (d: DemoDraft) => idbPut('demo', DEMO_KEY, d);
export const deleteDemoDraft = () => idbDelete('demo', DEMO_KEY).catch(() => undefined);
